import { cloneDeep } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Text } from '~/eds/src/components/Text/Text';
import { Nullable } from '~/types/Nullable';
import {
  TurnTrackingHistoryType,
  TurnTrackingPartyType,
} from '~/types/TurnTracking';

import { EditTimeFields } from './EditTimeFields';
import { PartySelectField } from './PartySelectField';
import { TurnTrackingHistory } from './TurnTrackingHistory';
import { partyNameFromPartyIdAndParties } from './utils';

interface Props {
  history: TurnTrackingHistoryType[];
  parties: TurnTrackingPartyType[];
  disableSave: boolean;
  entry: TurnTrackingHistoryType;
  entryReference: TurnTrackingHistoryType;
  previousEntry: Nullable<TurnTrackingHistoryType>;
  nextEntry: Nullable<TurnTrackingHistoryType>;
  errorMessages: string[];
  isCurrentTurn: boolean;
  isLoadingActionButton: boolean;
  isCreatingNewEntry: boolean;
  isTicketFinished: boolean;
  isHidden?: boolean;
  onHide?: () => void;
  onBack: () => void;
  onSubmit: () => void;
  onEditEntry: (entry: TurnTrackingHistoryType) => void;
  onToggleCreatingNewEntry: (bool: boolean) => void;
}

export const EditCyclePanel = ({
  history,
  parties,
  disableSave,
  entry,
  entryReference,
  previousEntry,
  nextEntry,
  errorMessages,
  isCurrentTurn,
  isLoadingActionButton,
  isCreatingNewEntry,
  isHidden,
  isTicketFinished,
  onHide,
  onBack,
  onSubmit,
  onEditEntry,
  onToggleCreatingNewEntry,
}: Props) => {
  const intl = useIntl();
  const [isPartySelected, setIsPartySelected] = useState(true);
  const currentPartyId = entryReference.partyId;
  const alternatePartyId = parties.filter(
    (party) => party.id !== currentPartyId,
  )[0].id;
  const partyName = partyNameFromPartyIdAndParties(entry.partyId, parties);
  return (
    <Panel
      width="560px"
      mode="floating"
      backAction={{
        text: intl.formatMessage({
          id: 'EditCyclePanel.backButton',
          defaultMessage: 'Back',
          description: 'Back button text',
        }),
        onClick: onBack,
      }}
      title={intl.formatMessage({
        id: 'EditCyclePanel.title',
        defaultMessage: 'Edit Cycle Time',
        description: 'Panel title for editing cycle time',
      })}
      footer={{
        actions: [
          {
            text: intl.formatMessage({
              id: 'EditCyclePanel.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            }),
            level: 'secondary',
            onClick: onBack,
          },
          {
            text: isCreatingNewEntry
              ? intl.formatMessage({
                  id: 'EditCyclePanel.createTurnButton',
                  defaultMessage: 'Create Turn',
                  description: 'Create turn button text',
                })
              : intl.formatMessage({
                  id: 'EditCyclePanel.saveButton',
                  defaultMessage: 'Save',
                  description: 'Save button text',
                }),
            level: 'primary',
            disabled: (disableSave && !isCreatingNewEntry) || !isPartySelected,
            onClick: onSubmit,
            isLoading: isLoadingActionButton,
          },
        ],
      }}
      {...(!!onHide && { hidden: { isHidden, onHide } })}
    >
      <Layout direction="column" spacing={8}>
        {nextEntry && (
          <Layout direction="column">
            <Text variant="section">
              {intl.formatMessage({
                id: 'EditCyclePanel.nextTurnSection',
                defaultMessage: 'Next Turn',
                description: 'Section header for next turn',
              })}
            </Text>
            <TurnTrackingHistory
              parties={parties}
              historyEntry={nextEntry}
              isTicketFinished={isTicketFinished}
            />
          </Layout>
        )}
        <Layout direction="column">
          {!isCurrentTurn && (
            <Text variant="section">
              {intl.formatMessage({
                id: 'EditCyclePanel.currentTurnSection',
                defaultMessage: 'Current Turn',
                description: 'Section header for current turn',
              })}
            </Text>
          )}
          {isCurrentTurn && (
            <Layout pb={4}>
              <PartySelectField
                history={history}
                parties={parties}
                onChange={(partyId) => {
                  if (partyId === currentPartyId) {
                    setIsPartySelected(true);
                    onToggleCreatingNewEntry(false);
                    onEditEntry(cloneDeep(entryReference));
                  } else if (partyId === alternatePartyId) {
                    setIsPartySelected(true);
                    onToggleCreatingNewEntry(true);
                    onEditEntry({
                      ...entry,
                      partyId: partyId!,
                      startTime: new Date(),
                    });
                  } else {
                    setIsPartySelected(false);
                  }
                }}
              />
            </Layout>
          )}
          <EditTimeFields
            partyName={partyName}
            entry={entry}
            disableInput={isCreatingNewEntry}
            onChange={onEditEntry}
          />
          {errorMessages.map((message: string) => (
            <Text variant="tiny" color="status.danger" role="alert">
              {message}
            </Text>
          ))}
        </Layout>
        {previousEntry && (
          <Layout direction="column">
            <Text variant="section">
              {intl.formatMessage({
                id: 'EditCyclePanel.previousTurnSection',
                defaultMessage: 'Previous Turn',
                description: 'Section header for previous turn',
              })}
            </Text>
            <TurnTrackingHistory
              parties={parties}
              historyEntry={previousEntry}
              isTicketFinished={isTicketFinished}
            />
          </Layout>
        )}
      </Layout>
    </Panel>
  );
};
